import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { errorText, setupApi } from '../../services/setupApi';
import type { SetupView } from '../../services/setupApi';
import { AboutStep, AppsStep, WelcomeStep } from './FirstSteps';
import { ConnectStep, IdeasStep, PermissionsStep, StartStep } from './LaterSteps';

/*
 * The setup every company member walks the first time they come in, whatever
 * their role and however they arrived. Same screens for everyone; the server
 * says what is already known and whether the department can be changed.
 */
const SetupPage: React.FC = () => {
  const [view, setView] = useState<SetupView | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setupApi.begin()
      .then(({ data }) => {
        if (!data.applies || data.setup?.finished_at) {
          window.location.href = '/dashboard';
          return;
        }
        setView(data);
      })
      .catch((err) => setError(errorText(err, 'Could not open your setup.')));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [view?.setup?.step]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4] p-6 text-center">
        <div>
          <p className="text-[15px] text-[#18191d]">{error}</p>
          <a href="/dashboard" className="mt-3 inline-block text-[13px] font-semibold underline underline-offset-4">Go to the dashboard</a>
        </div>
      </div>
    );
  }
  if (!view?.setup) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><Loader2 size={22} className="animate-spin text-[#92949d]" /></div>;
  }

  const props = { view, onView: setView };
  const steps: Record<string, React.ReactNode> = {
    welcome: <WelcomeStep {...props} />,
    about: <AboutStep {...props} />,
    apps: <AppsStep {...props} />,
    permissions: <PermissionsStep {...props} />,
    ideas: <IdeasStep {...props} />,
    connect: <ConnectStep {...props} />,
    start: <StartStep {...props} />,
  };
  return <>{steps[view.setup.step] || steps.welcome}</>;
};

export default SetupPage;
